import React, { Ref, useState } from 'react';
import {
  ActionTypes,
  EarningRuleActionTransImpact,
  RuleAttributes,
  RuleType,
  TransactionAction,
} from '@loyalty/shared-common/rule-api-client';
import { Box, TableCell, TableRow } from '@mui/material';
import { CurrencyAttributes } from '@loyalty/shared-common/gamification-api-client';
import {
  DraggableProvidedDragHandleProps,
  DraggableProvidedDraggableProps,
} from 'react-beautiful-dnd';
import { Feature, extractErrorMessage } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  QlfConfirmationDialog,
  colorPalette,
  useAlerts,
  useConfirmationDialog,
} from '@qualifio/ui-commons';
import { faGripVertical } from '@fortawesome/pro-solid-svg-icons/faGripVertical';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CopyIconButton, DeleteIconButton, EditIconButton } from '../shared/TableIconButton';
import { CurrencyImage } from '../shared/currency/CurrencyImage';
import { EarningRuleModal } from './EarningRuleModal';
import { Loader } from '../shared/layout/Loader';
import { RuleConflictModal, RuleConflictModalProps } from '../rules/components/RuleConflictModal';
import { RuleLabel } from '../rules/components/RuleLabel';
import { SxStyles } from '../shared/utils/types';
import { eventSourceIcons, useEventSourceNames } from '../rules/rule-table-constants';
import { useApi } from '../shared/api-client/ApiClientProvider';
import { useCurrencies } from '../currency/CurrenciesContext';
import { useFeatures } from '../features/FeaturesContext';

const styles: SxStyles = {
  iconButton: {
    color: colorPalette.grey.main,
    backgroundColor: 'inherit',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: 'inherit',
      color: colorPalette.grey.darkHover,
    },
  },
};

export interface EarningTransactionActionForm {
  kind: ActionTypes;
  impact?: EarningRuleActionTransImpact;
}

interface EarningRuleTableRowProps {
  rule: RuleAttributes;
  dragHandleProps?: DraggableProvidedDragHandleProps | undefined | null;
  onCloned: () => void;
  onDeleted: () => void;
  draggableRef: Ref<any>;
  draggableProps: DraggableProvidedDraggableProps;
}

export function EarningRuleTableRow({
  rule,
  onCloned,
  onDeleted,
  dragHandleProps,
  draggableRef,
  draggableProps,
}: EarningRuleTableRowProps) {
  const { t } = useTranslation();
  const [conflictModalIsOpen, setConflictModalIsOpen] = useState(false);
  const [conflictDialogProps, setConflictDialogProps] = useState<
    Omit<RuleConflictModalProps, 'isOpen' | 'setIsOpen'>
  >({
    ruleType: RuleType.EARN,
  });
  const { currencies } = useCurrencies();
  const { features } = useFeatures();
  const { write: earningRuleWrite, remove: earningRuleRemove } = features[Feature.EARNING_RULE];

  const ruleCurrencies = [
    ...new Set(
      rule.actions
        .filter((a): a is TransactionAction => a.kind === ActionTypes.TRANSACTION)
        .map(({ impact }: TransactionAction) =>
          currencies.find(({ id }) => id === impact.currency),
        ),
    ),
  ];

  const [editModalOpen, setEditModalOpen] = useState(false);

  const handleEdit = () => setEditModalOpen(true);

  const sourceTranslations = useEventSourceNames();

  const { ruleApi } = useApi();
  const { notify } = useAlerts();
  const { isPending: isDeleting, mutate: deleteRule } = useMutation({
    mutationKey: ['rule.delete'],

    mutationFn: () =>
      ruleApi
        .deleteRule(rule.id, rule.version)
        .then(onDeleted)
        .catch(error => {
          if (error.response?.status === 409) {
            setConflictDialogProps({
              ...conflictDialogProps,
              lastModification: error.response?.data?.details,
            });
            setConflictModalIsOpen(true);
          } else {
            throw error;
          }
        }),

    onError: (err: any) => notify(extractErrorMessage(err) || t('error.page'), 'error'),
  });
  const { isPending: isCloning, mutate: cloneRule } = useMutation({
    mutationKey: ['rule', rule, 'clone'],
    mutationFn: () =>
      ruleApi.addRule(rule.programId, { ...rule, name: `${rule.name} (copy)` }).then(onCloned),
    onError: (err: any) => notify(extractErrorMessage(err) || t('error.page'), 'error'),
  });

  const { confirmationDialogState, setConfirmationDialogState } = useConfirmationDialog();
  const openDialog = () => {
    setConfirmationDialogState({
      title: t('rules.confirm.deletion.title'),
      content: t('rules.confirm.deletion.message'),
      confirmButtonText: t('rules.confirm.deletion.confirmation'),
      cancelButtonText: t('generic.actions.cancel'),
      open: true,
      onConfirm: () => deleteRule(),
    });
  };

  return (
    <>
      <RuleConflictModal
        {...conflictDialogProps}
        setIsOpen={setConflictModalIsOpen}
        isOpen={conflictModalIsOpen}
      />
      <TableRow ref={draggableRef} {...draggableProps}>
        <TableCell>
          {earningRuleWrite && (
            <Box sx={styles.iconButton} {...dragHandleProps}>
              <FontAwesomeIcon icon={faGripVertical} />
            </Box>
          )}
        </TableCell>
        <TableCell>
          <RuleLabel rule={rule} />
        </TableCell>
        <TableCell>
          <Box display="flex" alignItems="center">
            <Box fontWeight="bold" mr={1} component="span">
              {eventSourceIcons[rule.eventSource]}
            </Box>{' '}
            {sourceTranslations[rule.eventSource]}
          </Box>
        </TableCell>
        <TableCell>
          <Box display="flex" alignItems="center">
            {ruleCurrencies
              .filter((c): c is CurrencyAttributes => !!c)
              .map(currency => (
                <CurrencyImage key={currency.id} currency={currency} sx={{ marginRight: 0.5 }} />
              ))}
          </Box>
        </TableCell>
        <TableCell>
          <Box display="flex">
            <EditIconButton onClick={handleEdit} disabled={!earningRuleWrite} />
            {isCloning ? (
              <Loader />
            ) : (
              <CopyIconButton onClick={() => cloneRule()} disabled={!earningRuleWrite} />
            )}
            <QlfConfirmationDialog {...confirmationDialogState} />
            {!isDeleting ? (
              <DeleteIconButton disabled={!earningRuleRemove} onClick={openDialog} />
            ) : (
              <Loader />
            )}
          </Box>
        </TableCell>
        {/*TODO probably more efficient to have a single edit modal instance and just swap the input rule, but this will work for now*/}
        <EarningRuleModal rule={rule} isOpen={editModalOpen} setIsOpen={setEditModalOpen} />
      </TableRow>
    </>
  );
}
